import React, { useCallback, useEffect, useRef } from 'react';

import { LayoutChangeEvent, View } from 'react-native';

import Animated from 'react-native-reanimated';
import { useDispatch } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { STRIPE_TITLE_ID, STRIPE_TYPE } from 'config/testID';

import { SCREEN } from 'router/screens';
import { ModularScreens, RouteNavigationParams } from 'router/types';

import { StreamTypes } from 'store/player/actions.types';
import {
    MenuConfigurationItem,
    MixedItem,
    RBParsedRail,
} from 'store/railsBuilder/reducer.types';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';

import { ContentType } from 'services/data-manager/types';
import useGetDeepLinkTriggeredParams from 'services/uav/useGetDeepLinkTriggeredParams';
import useGetRailSelectionParams from 'services/uav/useGetRailSelectionParams';
import useGetRailViewParams from 'services/uav/useGetRailViewParams';
import useThirdPartyAppStartParams from 'services/uav/useGetThirdPartyAppStartParams';
import { logUavAnalytics } from 'services/uav/utils';

import isFunction from 'utils/function';

import {
    OnSetStripeLoadingStatus,
    RailLoadingStatus,
} from 'screens/Modular/types';
import { SearchOptions, SearchType } from 'screens/Search/types';

import Gallery from 'components/Gallery';
import GallerySkeletonLoading from 'components/Gallery/сomponents/GallerySkeletonLoading';
import Ribbon from 'components/Ribbon';
import {
    DeepLinkTriggered,
    ThirdPartyAppStartItem,
} from 'components/Ribbon/types';
import StripeSkeletonLoading from 'components/StripeSkeletonLoading';

import { StripeHeader } from '../StripeHeader';
import useStripeDataLoading from './hooks/useStripeDataLoading';
import useStripeLoadingAnimation from './hooks/useStripeLoadingAnimation';
import styles from './styles';

type Props<T = RBParsedRail> = {
    rail: T;
    templateId: MenuConfigurationItem['id'];
    navigation: StackNavigationProp<
        RouteNavigationParams,
        ModularScreens | SCREEN.DETAILS | SCREEN.SEARCH
    >;
    searchQuery?: string;
    searchType?: SearchType;
    searchOptions?: SearchOptions;
    loadingStatus: RailLoadingStatus;
    screenFocused: boolean;
    screenReaderEnabled: boolean;
    index: number;
    onSetStripeLoadingStatus: OnSetStripeLoadingStatus;
    onSetStripePosition?(
        stripeId: string,
        layout: LayoutChangeEvent['nativeEvent']['layout'],
    ): void;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
};

function Stripe({
    rail: {
        id: railId,
        properties: { ContentType: type = ContentType.Mixed },
        title,
        name,
    },
    templateId,
    rail,
    searchQuery,
    searchType,
    searchOptions,
    navigation,
    loadingStatus,
    screenFocused,
    screenReaderEnabled,
    index,
    onSetStripeLoadingStatus,
    onSetStripePosition,
    onShowPinDialog,
    onSetParentalControlCheckedCallback,
}: Props) {
    const dispatch = useDispatch();

    const { getRailViewParams } = useGetRailViewParams();
    const { getRailSelectionParams } = useGetRailSelectionParams();
    const { getDeepLinkTriggeredParams } = useGetDeepLinkTriggeredParams();
    const { getThirdPartyAppStartParams } = useThirdPartyAppStartParams();
    const { opacitySharedValue, animatedStyle } =
        useStripeLoadingAnimation(loadingStatus);

    const isGalleryStripe = type === ContentType.Gallery;
    const stripeIsLoading =
        loadingStatus === RailLoadingStatus.IDLE ||
        loadingStatus === RailLoadingStatus.TO_LOAD ||
        loadingStatus === RailLoadingStatus.LOADING;

    const {
        data: modularStripeData,
        hasNext: modularStripeHasNext,
        nextUpdateTime,
        onReloadStripeData,
    } = useStripeDataLoading({
        rail,
        templateId,
        loadingStatus,
        onSetStripeLoadingStatus,
    });

    const { sections: searchStripeData, hasNext: searchStripeHasNext } = rail;

    const data = searchQuery != null ? searchStripeData : modularStripeData;

    const hasNext =
        searchQuery != null ? searchStripeHasNext : modularStripeHasNext;

    const onRailsSelection = useCallback(
        ({
            id,
            playbackType,
            index: railIndex,
        }: {
            id: string;
            playbackType: StreamTypes;
            index: number;
        }) => {
            logUavAnalytics(
                getRailSelectionParams(
                    templateId,
                    railId,
                    railIndex,
                    id,
                    playbackType,
                ),
            );
        },
        [getRailSelectionParams, templateId, railId],
    );

    const onThirdPartyAppStart = useCallback(
        ({
            appReference,
            deeplinkProviderId,
            deepLinkid,
            providerResourceId,
            appName,
        }: ThirdPartyAppStartItem) => {
            logUavAnalytics(
                getThirdPartyAppStartParams({
                    templateId,
                    railId,
                    appReference,
                    deeplinkProviderId,
                    deepLinkid,
                    providerResourceId,
                    appName,
                    playbackType: undefined,
                }),
            );
        },
        [getThirdPartyAppStartParams, templateId, railId],
    );

    const onDeepLinkTriggered = useCallback(
        (item: DeepLinkTriggered) => {
            logUavAnalytics(
                getDeepLinkTriggeredParams({
                    templateId,
                    railId,
                    ...item,
                }),
            );
        },
        [templateId, railId, getDeepLinkTriggeredParams],
    );

    const handleOnViewLayout = useCallback(
        ({ nativeEvent: { layout } }) => {
            onSetStripePosition?.(railId, layout);
        },
        [railId, onSetStripePosition],
    );

    const renderStripe = useCallback(() => {
        const testID = `${STRIPE_TYPE}${type}`;

        if (isGalleryStripe) {
            return (
                <Gallery
                    testID={testID}
                    items={data as MixedItem[]}
                    navigation={navigation}
                    templateId={templateId}
                    railId={railId}
                    screenFocused={screenFocused}
                    screenReaderEnabled={screenReaderEnabled}
                    onRailsSelection={onRailsSelection}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    onThirdPartyAppStart={onThirdPartyAppStart}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                />
            );
        }

        if (type) {
            return (
                <Ribbon
                    testID={testID}
                    items={data as RBParsedRail['sections']}
                    metadataType={type}
                    templateId={templateId}
                    railId={railId}
                    onRailsSelection={onRailsSelection}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    onThirdPartyAppStart={onThirdPartyAppStart}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                    railTitle={title || name || ''}
                />
            );
        }

        return null;
    }, [
        type,
        data,
        railId,
        templateId,
        navigation,
        isGalleryStripe,
        screenFocused,
        screenReaderEnabled,
        onRailsSelection,
        onDeepLinkTriggered,
        onThirdPartyAppStart,
        onShowPinDialog,
        onSetParentalControlCheckedCallback,
    ]);

    const handleOpenCollection = useCallback(() => {
        if (type) {
            logUavAnalytics(getRailSelectionParams(templateId, railId, index));

            dispatch(
                startSentryTransaction(
                    SENTRY_TRANSACTIONS.CollectionPageRendering,
                ),
            );

            navigation.navigate(SCREEN.COLLECTION, {
                title: title || name || '',
                templateId,
                railId,
                searchQuery,
                searchType,
                searchOptions,
                stripeIndex: index,
            });
        }
    }, [
        railId,
        templateId,
        title,
        searchQuery,
        searchType,
        searchOptions,
        index,
    ]);

    useEffect(() => {
        if (
            screenFocused &&
            isFunction(onReloadStripeData) &&
            [
                ContentType.NPVR,
                ContentType.NowNext,
                ContentType.ContinueWatching,
            ].includes(type)
        ) {
            onReloadStripeData();
        }
    }, [screenFocused]);

    useEffect(() => {
        if (
            (loadingStatus === RailLoadingStatus.EMPTY ||
                loadingStatus === RailLoadingStatus.FAILURE) &&
            !data?.length
        ) {
            // Inform Modular page that stripe is empty and had been "removed" from the screen
            onSetStripePosition?.(railId, { height: 0, y: 0, width: 0, x: 0 });
        }
    }, [data?.length, loadingStatus]);

    useEffect(() => {
        if (loadingStatus === RailLoadingStatus.LOADED && data?.length) {
            logUavAnalytics(getRailViewParams(templateId, railId, index));
        }
    }, [loadingStatus, data?.length]);

    const interval = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (
            isFunction(onReloadStripeData) &&
            type === ContentType.NowNext &&
            screenFocused &&
            interval.current === null &&
            nextUpdateTime
        ) {
            interval.current = setInterval(() => {
                if (Date.now() >= nextUpdateTime) {
                    onReloadStripeData();
                }
            }, 5000);
        }

        return () => {
            if (interval.current) {
                clearInterval(interval.current);
                interval.current = null;
            }
        };
    }, [screenFocused, nextUpdateTime, type, onReloadStripeData]);

    if (
        (loadingStatus === RailLoadingStatus.EMPTY ||
            loadingStatus === RailLoadingStatus.FAILURE) &&
        !data?.length
    ) {
        return null;
    }

    return (
        <View style={styles.container} onLayout={handleOnViewLayout}>
            {!isGalleryStripe && (
                <Animated.View style={animatedStyle}>
                    <StripeHeader
                        testID={`${STRIPE_TITLE_ID}_${title}`.replace(/ /g, '')}
                        title={title || name || ''}
                        collectionAvailable={hasNext}
                        loading={stripeIsLoading}
                        onOpenCollection={handleOpenCollection}
                    />
                </Animated.View>
            )}
            {stripeIsLoading && isGalleryStripe && (
                <GallerySkeletonLoading
                    opacitySharedValue={opacitySharedValue}
                />
            )}
            {stripeIsLoading && !isGalleryStripe && (
                <StripeSkeletonLoading
                    railId={railId}
                    contentType={type}
                    opacitySharedValue={opacitySharedValue}
                />
            )}
            {loadingStatus === RailLoadingStatus.LOADED && renderStripe()}
        </View>
    );
}

export default React.memo(Stripe);
